import React, { useEffect, useRef, useState } from 'react';
import type { SyncDestination } from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { HelperText } from '../../../form-kit';
import { LinkField, LinkRow } from '../../../onboarding/invite/invite-qr-modal';
import { destinationName, exportHint } from '../../../../utils/stays-sync';
import { Reveal } from './sync.styled';

/**
 * A NEW EXPORT LINK, SHOWN ONCE (plan S5, screens §1; SD-S5b): the read-only
 * link with Copy → "Copied" for 1800 ms (invite-qr-modal.tsx's pattern and
 * parts), where to paste it, and "Done". The link lives in this component's
 * props only — it is never stored, and closing the panel forgets it.
 */

export interface ExportRevealProps {
  destination: SyncDestination;
  url: string;
  onDone: () => void;
}

export const ExportReveal: React.FC<ExportRevealProps> = ({ destination, url, onDone }) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFailed(false);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const name = destinationName(destination);

  return (
    <Reveal data-testid={`sync-export-reveal-${destination}`}>
      <LinkRow>
        <LinkField
          readOnly
          value={url}
          aria-label={`The ${name} calendar link`}
          onFocus={e => e.target.select()}
        />
        <AppButton variant="secondary" size="compact" type="button" aria-label={`Copy the ${name} calendar link`} onClick={() => void copy()}>
          <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
        </AppButton>
      </LinkRow>
      {copyFailed ? <HelperText role="alert">Copying did not work here. Select the link and copy it yourself.</HelperText> : null}
      <HelperText>{exportHint(destination)}</HelperText>
      <div>
        <AppButton variant="primary" size="compact" type="button" aria-label={`Done with the ${name} link`} onClick={onDone}>
          Done
        </AppButton>
      </div>
    </Reveal>
  );
};
